import React, {Suspense, lazy, useEffect, useState} from "react";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import {useAppDispatch} from "store/hooks";
import {authApi} from "store/services/authApi";
import {setCredentials, logout, setVerified} from "store/slices/authSlice";
import LoadingScreen from "components/Loading/LoadingScreen";
import {ThemeProvider} from "contexts/ThemeContext";
import PublicLayout from "./layouts/Public";

const AdminLayout = lazy(() => import("layouts/Admin"));
const EmployerLayout = lazy(() => import("layouts/Employer"));
const AuthLayout = lazy(() => import("layouts/Auth"));

const isProtectedAppPath = (pathname: string) =>
    pathname.startsWith("/admin") || pathname.startsWith("/employer");

const App = () => {
    const dispatch = useAppDispatch();
    const [isInitializing, setIsInitializing] = useState(() =>
        isProtectedAppPath(window.location.pathname)
    );

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const user = await dispatch(
                    authApi.endpoints.getCurrentUser.initiate(undefined, {forceRefetch: true})
                ).unwrap();

                if (user) {
                    dispatch(setCredentials({user}));
                    const userIsVerified = !!(user.account_status?.id &&
                        user.account_status.status?.toLowerCase() !== "pending");
                    dispatch(setVerified(userIsVerified));
                } else {
                    dispatch(logout());
                }
            } catch (error) {
                dispatch(logout());
            } finally {
                setIsInitializing(false);
            }
        };

        checkAuth();
    }, [dispatch]);

    if (isInitializing) {
        return <LoadingScreen/>;
    }

    return (
        <BrowserRouter>
            <ThemeProvider>
                <Suspense fallback={<LoadingScreen/>}>
                    <Routes>
                        <Route path="/admin/*" element={<AdminLayout/>}/>
                        <Route path="/employer/*" element={<EmployerLayout/>}/>
                        <Route path="/auth/*" element={<AuthLayout/>}/>
                        <Route path="/*" element={<PublicLayout/>}/>
                    </Routes>
                </Suspense>
            </ThemeProvider>
        </BrowserRouter>
    );
};

export default App;
